import { openAuthModal } from "@blocks/_shared/auth/use-auth-modal";
import { useEnrollment } from "@blocks/_shared/hooks/use-enrollment";
import { createRoot } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { Gift } from "lucide-react";
/**
 * Product Points Display Block - Frontend View
 *
 * Client-side rendering for the product points display.
 * Reads product data from data attributes and displays appropriate text.
 */
import type React from "react";
import "./style.scss";

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	memberText: string;
	invitationText: string;
	showZeroPoints: boolean;
}

interface ProductData {
	productId: number;
	points: number;
	price: number;
	isVariable: boolean;
	creditsName: string;
}

interface ProductPointsConfig {
	creditsName: string | { default: string };
	isAuthenticated: boolean;
	isMember: boolean;
	includeGuests: boolean;
	autoEnrollOnReg: boolean;
	onlyRewardKnown: boolean;
	joinUrl: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get localized text from a translatable object or string.
 */
function getLocalizedText(
	value: string | { default: string } | undefined,
	fallback: string,
): string {
	if (!value) return fallback;
	if (typeof value === "string") return value;
	return value.default || fallback;
}

/**
 * Replace placeholders in text with actual values.
 */
function replacePlaceholders(
	text: string,
	points: number,
	creditsName: string,
	joinUrl: string,
): string {
	let result = text
		.replace(/{{ points }}/g, String(points))
		.replace(/{{ credits_currency }}/g, creditsName);

	// Handle join link placeholder
	if (joinUrl) {
		result = result
			.replace(
				/{{ join_link }}/g,
				`<a href="${joinUrl}" class="leat-product-points__join-link">`,
			)
			.replace(/{{ \/join_link }}/g, "</a>");
	} else {
		// Remove join link placeholders if no URL
		result = result
			.replace(/{{ join_link }}/g, "")
			.replace(/{{ \/join_link }}/g, "");
	}

	return result;
}

// ============================================================================
// Main Component
// ============================================================================

interface ProductPointsViewProps {
	attributes: Attributes;
	productData: ProductData;
	config: ProductPointsConfig;
}

const ProductPointsView: React.FC<ProductPointsViewProps> = ({
	attributes,
	productData,
	config,
}) => {
	const { status } = useEnrollment();

	// Don't show if points are 0 and showZeroPoints is false
	if (productData.points <= 0 && !attributes.showZeroPoints) {
		return null;
	}

	// Get credits name
	const creditsName =
		productData.creditsName || getLocalizedText(config.creditsName, "points");

	// Determine which text to show based on enrollment status
	const isMember = status === "enrolled";
	const isLoggedInNotEnrolled = status === "loggedInNotEnrolled";
	const text = isMember ? attributes.memberText : attributes.invitationText;

	// Replace placeholders
	const displayText = replacePlaceholders(
		text,
		productData.points,
		creditsName,
		config.joinUrl,
	);

	return (
		<div className="leat-product-points__content">
			<Gift className="leat-product-points__icon" size={20} />
			<p
				className="leat-product-points__text"
				dangerouslySetInnerHTML={{ __html: displayText }}
			/>
			{isLoggedInNotEnrolled && (
				<button
					type="button"
					className="leat-product-points__join-button"
					onClick={() => openAuthModal({ intent: "join" })}
				>
					{__("Join Now", "leat-crm")}
				</button>
			)}
		</div>
	);
};

// ============================================================================
// Initialization
// ============================================================================

export function initProductPointsBlocks(): void {
	const blocks = document.querySelectorAll<HTMLElement>(
		".wp-block-leat-product-points",
	);

	// Get global config
	const config: ProductPointsConfig = (window as any)
		.leatProductPointsConfig || {
		creditsName: "points",
		isAuthenticated: false,
		isMember: false,
		includeGuests: false,
		autoEnrollOnReg: false,
		onlyRewardKnown: false,
		joinUrl: "",
	};

	for (const block of blocks) {
		// Parse attributes from data attributes
		const attributes: Attributes = {
			memberText:
				block.dataset.memberText ||
				"Earn {{ points }} {{ credits_currency }} with this purchase",
			invitationText:
				block.dataset.invitationText ||
				"{{ join_link }}Join our program{{ /join_link }} to earn {{ points }} {{ credits_currency }} with this purchase",
			showZeroPoints: block.dataset.showZeroPoints === "true",
		};

		// Parse product data from data attributes
		const productData: ProductData = {
			productId: Number(block.dataset.productId) || 0,
			points: Number(block.dataset.points) || 0,
			price: Number(block.dataset.price) || 0,
			isVariable: block.dataset.isVariable === "true",
			creditsName: block.dataset.creditsName || "",
		};

		// Only render if we have valid product data
		if (productData.productId > 0) {
			createRoot(block).render(
				<ProductPointsView
					attributes={attributes}
					productData={productData}
					config={config}
				/>,
			);
		}
	}
}

// Auto-initialize
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initProductPointsBlocks);
} else {
	initProductPointsBlocks();
}

// Extend window interface for config
declare global {
	interface Window {
		leatProductPointsConfig?: ProductPointsConfig;
		leat?: {
			userId?: number;
			loggedIn?: string;
		};
	}
}
